import { useState, useCallback } from "react"
import { createElementId } from "utils/idGenerators"
import {
  SlideElement,
  ELEMENT_TYPE,
  TEXT_ALIGN,
  BaseSlideElement,
  TextSlideElementProps,
  ImageSlideElementProps,
  TableSlideElementProps,
  ChartSlideElementProps,
} from "types/ReportingDashboard/elements.types"
import { Coordinates, DefaultElementSizes } from "./useElementConstraints"
import { CanvasDimensions } from "./useCanvasDimensions"

/**
 * Drag offset coordinates
 */
export interface DragOffset {
  x: number
  y: number
}

/**
 * Dropped item data structure from external drag source
 */
export interface DroppedItemData {
  type: ELEMENT_TYPE
  text?: string
}

/**
 * Default text formatting properties
 */
export interface TextFormatting {
  isBold: boolean
  isItalic: boolean
  isUnderline: boolean
  textAlign: TEXT_ALIGN
  color: string
  fontSize: number
}

/**
 * Return type for useDragAndDrop hook
 */
export interface DragAndDropResult {
  draggedElement: SlideElement | null
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void
  handleElementDragStart: (
    element: SlideElement,
    e: React.DragEvent<HTMLDivElement>
  ) => void
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void
}

/**
 * Custom hook for managing drag-and-drop functionality on the canvas.
 *
 * Handles both dragging existing elements to new positions and dropping new elements
 * from external sources (like a toolbar). Manages drag state, coordinate transformations,
 * and element creation with proper constraint application. Uses type-safe element creation
 * with proper discriminated unions for each element type.
 *
 * @param elements - Current array of canvas elements
 * @param onElementsChange - Callback to update the elements array
 * @param isTemplateMode - Whether canvas is in template editing mode
 * @param isTemplateElement - Function to check if element is from template
 * @param snapToGrid - Function to snap coordinates to grid
 * @param constrainToBoundaries - Function to apply position constraints
 * @param canvasDimensions - Current canvas pixel dimensions {width, height}
 * @param canvasRef - Reference to the canvas DOM element
 * @param POWERPOINT_WIDTH_UNITS - Canvas width in abstract units
 * @param POWERPOINT_HEIGHT_UNITS - Canvas height in abstract units
 * @param DEFAULT_ELEMENT_SIZES - Default dimensions for each element type
 *
 * @returns Drag and drop handlers and state
 *
 * @example
 * const {
 *   draggedElement,
 *   handleDragOver,
 *   handleElementDragStart,
 *   handleDrop
 * } = useDragAndDrop(
 *   elements, setElements, isTemplateMode, isTemplateElement,
 *   snapToGrid, constrainToBoundaries, canvasDimensions, canvasRef,
 *   1440, 810, DEFAULT_ELEMENT_SIZES
 * )
 *
 * // Use in canvas component
 * <div onDragOver={handleDragOver} onDrop={handleDrop}>
 *   {elements.map(element =>
 *     <div onDragStart={(e) => handleElementDragStart(element, e)}>
 *       {element.content}
 *     </div>
 *   )}
 * </div>
 */
export const useDragAndDrop = (
  elements: SlideElement[],
  onElementsChange: (elements: SlideElement[]) => void,
  isTemplateMode: boolean,
  isTemplateElement: (element: SlideElement) => boolean,
  snapToGrid: (x: number, y: number) => Coordinates,
  constrainToBoundaries: (
    id: string | number,
    x: number,
    y: number
  ) => Coordinates,
  canvasDimensions: CanvasDimensions,
  canvasRef: React.RefObject<HTMLDivElement | null>,
  POWERPOINT_WIDTH_UNITS: number,
  POWERPOINT_HEIGHT_UNITS: number,
  DEFAULT_ELEMENT_SIZES: DefaultElementSizes
): DragAndDropResult => {
  const [draggedElement, setDraggedElement] = useState<SlideElement | null>(
    null
  )
  const [dragOffset, setDragOffset] = useState<DragOffset>({ x: 0, y: 0 })

  // Default formatting for text elements
  const DEFAULT_TEXT_FORMATTING: TextFormatting = {
    isBold: false,
    isItalic: false,
    isUnderline: false,
    textAlign: TEXT_ALIGN.LEFT,
    color: "#000000",
    fontSize: 16,
  }

  // Override formatting for heading elements
  const HEADING_FORMATTING_OVERRIDES: Partial<TextFormatting> = {
    isBold: true,
    fontSize: 24,
  }

  /**
   * Handles drag over events on the canvas.
   *
   * Prevents default behavior to allow dropping and optionally provides visual feedback.
   *
   * @param e - The drag over event
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault()
    },
    []
  )

  /**
   * Initiates dragging for an existing element on the canvas.
   *
   * Calculates the drag offset and stores the dragged element state.
   * Prevents dragging template elements when not in template mode.
   *
   * @param element - The element being dragged
   * @param e - The drag start event
   */
  const handleElementDragStart = useCallback(
    (element: SlideElement, e: React.DragEvent<HTMLDivElement>): void => {
      // Prevent dragging template elements when not in template mode
      if (!isTemplateMode && isTemplateElement(element)) {
        e.preventDefault()
        return
      }
      e.stopPropagation()

      // Calculate drag offset from mouse position to element's top-left
      const elementRect = e.currentTarget.getBoundingClientRect()
      const offsetX = e.clientX - elementRect.left
      const offsetY = e.clientY - elementRect.top

      setDragOffset({ x: offsetX, y: offsetY })
      setDraggedElement(element)
    },
    [isTemplateMode, isTemplateElement]
  )

  /**
   * Creates a new element with proper typing based on element type.
   * Uses BaseSlideElement from the official types for consistency.
   *
   * @param elementType - Type of element to create
   * @param baseProps - Base properties using BaseSlideElement
   * @param droppedText - Text content for text-based elements
   * @returns Properly typed SlideElement
   */
  const createTypedElement = useCallback(
    (
      elementType: ELEMENT_TYPE,
      baseProps: BaseSlideElement,
      droppedText: string
    ): SlideElement => {
      switch (elementType) {
        case ELEMENT_TYPE.TEXT:
        case ELEMENT_TYPE.HEADING:
          return {
            ...baseProps,
            type: elementType,
            text: droppedText,
          } as TextSlideElementProps

        case ELEMENT_TYPE.IMAGE:
          return {
            ...baseProps,
            type: elementType,
            imageSrc: null,
            // Remove text properties for image elements
            text: undefined,
          } as ImageSlideElementProps

        case ELEMENT_TYPE.TABLE:
          return {
            ...baseProps,
            type: elementType,
            // Remove text properties for table elements
            text: undefined,
          } as TableSlideElementProps

        case ELEMENT_TYPE.CHART:
        case ELEMENT_TYPE.PIE_CHART:
        case ELEMENT_TYPE.TIME_SERIES_CHART:
          return {
            ...baseProps,
            type: elementType,
            // Remove text properties for chart elements
            text: undefined,
          } as ChartSlideElementProps

        default:
          throw new Error(`Unsupported element type: ${elementType}`)
      }
    },
    []
  )

  /**
   * Handles drop events on the canvas.
   *
   * Supports two scenarios:
   * 1. Moving an existing element to a new position
   * 2. Creating a new element from external drag data (e.g., from toolbar)
   *
   * Applies coordinate transformations, grid snapping, and boundary constraints.
   * Creates new elements with appropriate default properties and formatting using
   * type-safe discriminated unions.
   *
   * @param e - The drop event
   */
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault()
      if (!canvasRef.current) return

      // Calculate drop coordinates in canvas space
      const rect = canvasRef.current.getBoundingClientRect()
      const dropX_px = e.clientX - rect.left
      const dropY_px = e.clientY - rect.top

      // Convert pixel coordinates to abstract units
      const scaleX = canvasDimensions.width
        ? POWERPOINT_WIDTH_UNITS / canvasDimensions.width
        : 1
      const scaleY = canvasDimensions.height
        ? POWERPOINT_HEIGHT_UNITS / canvasDimensions.height
        : 1
      const droppedX_units = dropX_px * scaleX
      const droppedY_units = dropY_px * scaleY

      if (draggedElement) {
        // Handle moving existing element
        if (!isTemplateMode && isTemplateElement(draggedElement)) {
          // Reset drag state if trying to move template element outside template mode
          setDraggedElement(null)
          setDragOffset({ x: 0, y: 0 })
          return
        }

        // Calculate new position accounting for drag offset
        const targetX = droppedX_units - dragOffset.x * scaleX
        const targetY = droppedY_units - dragOffset.y * scaleY

        // Apply grid snapping and boundary constraints
        const { x: snappedX, y: snappedY } = snapToGrid(targetX, targetY)
        const { x: constrainedX, y: constrainedY } = constrainToBoundaries(
          draggedElement.id,
          snappedX,
          snappedY
        )

        // Update element position
        onElementsChange(
          elements.map((el) =>
            el.id === draggedElement.id
              ? { ...el, x: constrainedX, y: constrainedY }
              : el
          )
        )

        // Reset drag state
        setDraggedElement(null)
        setDragOffset({ x: 0, y: 0 })
      } else {
        // Handle creating new element from external drag
        try {
          const droppedItemData = e.dataTransfer.getData("text/plain")
          if (!droppedItemData) return

          const droppedItem: DroppedItemData = JSON.parse(droppedItemData)
          const elementType = droppedItem.type

          // Validate element type and ensure it's supported
          if (
            !elementType ||
            elementType === ELEMENT_TYPE.DEFAULT ||
            !DEFAULT_ELEMENT_SIZES[elementType as keyof DefaultElementSizes]
          ) {
            console.warn(
              "Dropped item has invalid or unsupported type:",
              elementType
            )
            return
          }

          // Get default dimensions for element type
          const { width: initialWidth, height: initialHeight } =
            DEFAULT_ELEMENT_SIZES[elementType as keyof DefaultElementSizes]

          // Apply text formatting (for text-based elements)
          let formatting: TextFormatting = { ...DEFAULT_TEXT_FORMATTING }
          if (elementType === ELEMENT_TYPE.HEADING) {
            formatting = { ...formatting, ...HEADING_FORMATTING_OVERRIDES }
          }

          // Generate unique ID and apply constraints
          const newId = createElementId(elementType)
          const { x: snappedX, y: snappedY } = snapToGrid(
            droppedX_units,
            droppedY_units
          )
          const { x: constrainedX, y: constrainedY } = constrainToBoundaries(
            newId,
            snappedX,
            snappedY
          )

          // Create base properties using BaseSlideElement
          const baseElementProps: BaseSlideElement = {
            id: newId,
            type: elementType, // Will be overridden by createTypedElement
            x: constrainedX,
            y: constrainedY,
            width: initialWidth,
            height: initialHeight,
            isTemplateElement: isTemplateMode,
            ...formatting,
          }

          // Create properly typed element based on type
          const newElement = createTypedElement(
            elementType,
            baseElementProps,
            droppedItem.text || ""
          )

          // Add new element to canvas
          onElementsChange([...elements, newElement])
        } catch (err) {
          console.error("Failed to parse or add dropped element:", err)
        }
      }
    },
    [
      draggedElement,
      dragOffset,
      elements,
      isTemplateMode,
      isTemplateElement,
      snapToGrid,
      constrainToBoundaries,
      onElementsChange,
      canvasDimensions,
      canvasRef,
      POWERPOINT_WIDTH_UNITS,
      POWERPOINT_HEIGHT_UNITS,
      DEFAULT_ELEMENT_SIZES,
      createTypedElement,
    ]
  )

  return {
    draggedElement,
    handleDragOver,
    handleElementDragStart,
    handleDrop,
  }
}
